class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    this.skeletonLoader = null;
    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);
    this.onCartUpdateListener = this.onCartUpdate.bind(this);
    this.pageIsLoading = true;
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('load', () => { this.pageIsLoading = false; }, { once: true });
    document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.addEventListener('cart:refresh', this.onCartRefreshListener);
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdateListener
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.removeEventListener('cart:refresh', this.onCartRefreshListener);
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  onCartUpdate(event) {
    if (!this.open && !this.pageIsLoading) {
      const isInternalUpdate = event.sources && (
        event.sources.includes('cart-items') ||
        event.sources.includes('cart-refresh') ||
        event.sources.includes('la-dn-event')
      );
      if (!isInternalUpdate && event.cart && event.cart.item_count > 0) {
        this.show();
      }
    }

    if (!this.skeletonLoader) {
      this.skeletonLoader = new CartDrawerSkeletonLoader(this);
      this.skeletonLoader.apply();
    }
  }

  show(focusElement = null, animate = true) {
    document.body.classList.add('body-no-scroll');
    super.show(focusElement, animate);
    if (this.open) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }
  }

  hide() {
    document.body.classList.remove('body-no-scroll');
    super.hide();
  }

  removeSkeletonLoader() {
    if (this.skeletonLoader) {
      this.skeletonLoader.remove();
      this.skeletonLoader = null;
    }
  }

  rebindEvents() {
    const closeButton = this.querySelector('.drawer__close-btn');
    const overlay = this.querySelector('.fixed-overlay');

    const closeHandler = (event) => {
      event.preventDefault();
      this.hide();
    };

    if (closeButton) {
      closeButton.addEventListener('click', closeHandler);
    }
    if (overlay) {
      overlay.addEventListener('click', closeHandler);
    }
  }

  async onCartRefresh(event) {
    const cartId = `CartDrawer-${this.sectionId}`;
    const cartElement = document.getElementById(cartId);
    if (!cartElement) return;

    // Only open the drawer if explicitly requested AND page is fully loaded
    if (event.detail?.open && !this.open && !this.pageIsLoading) {
      this.show();
      // console.log('cart refresh -- test open drawer');
    }

    if (!this.skeletonLoader) {
      this.skeletonLoader = new CartDrawerSkeletonLoader(this);
      this.skeletonLoader.apply();
    }

    setTimeout(async () => {
      try {
        const response = await fetch(`${FoxTheme.routes.root_url}?section_id=${this.sectionId}`);
        const responseText = await response.text();
        const parser = new DOMParser();
        const parsedHTML = parser.parseFromString(responseText, 'text/html');
        const newCartContent = parsedHTML.getElementById(cartId);

        if (newCartContent) {
          cartElement.innerHTML = newCartContent.innerHTML;
        }

        // Check if cart is empty and update UI accordingly
        const cartResponse = await fetch(`${FoxTheme.routes.cart_url}.js`);
        const cartData = await cartResponse.json();

        // Publish cart update event to update the cart count
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          sources: 'cart-refresh',
          cart: cartData
        });

        if (cartData.item_count === 0) {
          const emptyCartElement = document.getElementById(`CartDrawerEmpty-${this.sectionId}`);
          const cartBodyElement = document.getElementById(`CartDrawerBody-${this.sectionId}`);
          const cartFooterElement = document.getElementById(`CartDrawerFooter-${this.sectionId}`);

          if (emptyCartElement) emptyCartElement.classList.remove('hidden');
          if (cartBodyElement) cartBodyElement.classList.add('hidden');
          if (cartFooterElement) cartFooterElement.classList.add('hidden');
        }

        this.rebindEvents();
        addDiscountTokensToCartItems();
        if (window.discountNinja?.api?.strikethroughPricing?.apply) {
          await window.discountNinja.api.strikethroughPricing.apply();
        }
      } catch (error) {
        // console.error('Error refreshing cart drawer:', error);
      } finally {
        this.removeSkeletonLoader();
      }
    }, 1500);
  }
}
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.onChange = this.onChange.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange, 300));
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate
    );

    this.cartItemProducts = this.querySelectorAll('.cart-item__product');
    this.cartItemQuantitys = this.querySelectorAll('.cart-item__quantity');

    // Initialize the skeleton loader when cart items are first loaded
    // this.applySkeletonLoader(); // This was removed as it's now handled by component logic

    const loadTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const template = element.querySelector('template');
        const templateContent = template && document.importNode(template.content, true);
        const parent = element.querySelector(parentSelector);

        if (parent && template && !parent.querySelector('.template-content')) {
          const contentWrapper = document.createElement('div');
          contentWrapper.classList.add('template-content', 'w-full');
          contentWrapper.appendChild(templateContent);
          parent.appendChild(contentWrapper);
        }
      });
    };

    const removeTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const parent = element.querySelector(parentSelector);
        const templateContent = parent?.querySelector('.template-content');

        if (templateContent) {
          parent.removeChild(templateContent);
        }
      });
    };

    const handleTabletMatch = () => {
      loadTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      removeTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const handleTabletUnmatch = () => {
      removeTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      loadTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
    FoxTheme.config.mqlTablet = mqlTablet.matches;

    if (FoxTheme.config.mqlTablet) {
      handleTabletMatch();
    } else {
      handleTabletUnmatch();
    }

    mqlTablet.onchange = (event) => {
      if (event.matches) {
        handleTabletMatch();
      } else {
        FoxTheme.config.mqlTablet = false;
        handleTabletUnmatch();
      }
    };

    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
  }

  cartUpdateUnsubscriber = undefined;

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate(event) {
    if (!this.isConnected || this.closest('cart-drawer') || this.closest('main-cart')) {
      return;
    }
    const sectionId = FoxTheme.utils.getSectionId(this);

    if (!event.cart.sections?.[sectionId]) return;

    const sectionToRender = new DOMParser().parseFromString(
      event.cart.sections[sectionId],
      'text/html'
    );
    const cartElement = this.closest('.cart');
    const updatedCartElement = sectionToRender.querySelector('.cart');

    if (cartElement && updatedCartElement) {
        cartElement.innerHTML = updatedCartElement.innerHTML;
    }
  }

  setValidity(target, message) {
    target.setCustomValidity(message);
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  validateQuantity(event) {
    const target = event.target;
    const inputValue = parseInt(target.value);
    const index = target.getAttribute('data-index');
    let message = '';

    if (inputValue < parseInt(target.getAttribute('data-min'))) {
      message = FoxTheme.quickOrderListStrings.min_error.replace('[min]', target.getAttribute('data-min'));
    } else if (inputValue > parseInt(target.max)) {
      message = FoxTheme.quickOrderListStrings.max_error.replace('[max]', target.max);
    } else if (inputValue % parseInt(target.step) !== 0) {
      message = FoxTheme.quickOrderListStrings.step_error.replace('[step]', target.step);
    }

    if (message) {
      this.setValidity(target, message);
    } else {
      target.setCustomValidity('');
      target.reportValidity();
      this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
    }
  }

  onCartError(errors, target) {
    const { input, line } = FoxTheme.utils.getLineItemFromTarget(target);
    if (input) {
      this.updateQuantity(
        line,
        input.defaultValue,
        document.activeElement.getAttribute('name'),
        input
      );
    }

    alert(errors);
  }

  async updateQuantity(line, quantity, name, target) {
    const mainCart = this.closest('main-cart');
    let mainCartSkeletonLoader;

    if (mainCart) {
      mainCartSkeletonLoader = new MainCartSkeletonLoader(mainCart);
      mainCartSkeletonLoader.apply();
    }

    try {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));

      const updateBody = JSON.stringify({
        line: parseInt(line, 10),
        quantity: parseInt(quantity, 10),
        sections,
        sections_url: window.location.pathname
      });
      const updateResponse = await fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body: updateBody } });
      const cartState = await updateResponse.json();

      if (cartState.errors) {
        this.onCartError(cartState.errors, target);
        return;
      }

      // Check if cart is now empty
      if (cartState.item_count === 0) {
        // Handle empty cart state
        const cartContainer = this.closest('.cart');
        if (cartContainer) {
          cartContainer.classList.add('is-empty');

          // Show empty cart message
          const emptyCart = cartContainer.querySelector('.cart__empty');
          if (emptyCart) {
            emptyCart.classList.remove('hidden');
          }

          // Hide cart form
          const cartForm = cartContainer.querySelector('.cart__form-wrapper');
          if (cartForm) {
            cartForm.classList.add('hidden');
          }

          // Hide cart footer
          const cartFooter = cartContainer.querySelector('.cart__footer');
          if (cartFooter) {
            cartFooter.classList.add('hidden');
          }
        }

        // Handle cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const sectionId = cartDrawer.getAttribute('data-section-id');
          const emptyCartElement = document.getElementById(`CartDrawerEmpty-${sectionId}`);
          const cartBodyElement = document.getElementById(`CartDrawerBody-${sectionId}`);
          const cartFooterElement = document.getElementById(`CartDrawerFooter-${sectionId}`);

          if (emptyCartElement) emptyCartElement.classList.remove('hidden');
          if (cartBodyElement) cartBodyElement.classList.add('hidden');
          if (cartFooterElement) cartFooterElement.classList.add('hidden');
        }
      }

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        sources: 'cart-items-quantity-update',
        cart: cartState,
        line: line,
        name: name
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartState } }));

    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        if (mainCartSkeletonLoader) {
          mainCartSkeletonLoader.remove();
        }
      }, 3000);
    }
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add('btn--loading');
      });
    }
  }
}
if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems = this.closest('cart-items');
      if (!cartItems) return;

      // Check if this is the last item in the cart
      const allCartItems = document.querySelectorAll('.cart-item');
      const isLastItem = allCartItems.length === 1;

      cartItems.updateQuantity(this.dataset.index, 0);

      if (isLastItem) {
        // If this is the last item, we need to force a refresh after a short delay
        // to ensure the empty cart state is properly displayed
        setTimeout(() => {
          // For main cart page
          const mainCart = document.querySelector('.cart');
          if (mainCart) {
            mainCart.classList.add('is-empty');
          }

          // For cart drawer
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            const sectionId = cartDrawer.getAttribute('data-section-id');
            const emptyCartElement = document.getElementById(`CartDrawerEmpty-${sectionId}`);
            const cartBodyElement = document.getElementById(`CartDrawerBody-${sectionId}`);
            const cartFooterElement = document.getElementById(`CartDrawerFooter-${sectionId}`);

            if (emptyCartElement) emptyCartElement.classList.remove('hidden');
            if (cartBodyElement) cartBodyElement.classList.add('hidden');
            if (cartFooterElement) cartFooterElement.classList.add('hidden');
          }
        }, 3500); // Slightly longer than the skeleton animation
      }
    });
  }
}
if (!customElements.get('cart-remove-item')) {
  customElements.define('cart-remove-item', CartRemoveItem, { extends: 'a' });
}

class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));

    if (this.button && this.cartNoteDetailsSummary) {
      this.button.addEventListener('click', () => {
        this.cartNoteDetailsSummary.close();
      });
    }
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } });
  }
}
if (!customElements.get('cart-note')) {
  customElements.define('cart-note', CartNote);
}

class CalculateShipping extends ModalComponent {
  constructor() {
    super();
    this.countryProvince = this.querySelector('country-province');
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, 'data-show'];
  }
  show() {
    super.show();
    this.setAttribute('data-show', true);
  }

  hide() {
    super.hide();
    this.setAttribute('data-show', false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'data-show' && newValue === 'true' && !this.isCountrySetup && this.countryProvince) {
      const template = this.countryProvince.querySelector('template');
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
if (!customElements.get('calculate-shipping')) {
  customElements.define('calculate-shipping', CalculateShipping);
}

class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));
    if (this.template && this.template === 'cart') {
      this.init();
    }
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.dataset.country)
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
if (!customElements.get('country-province')) {
  customElements.define('country-province', CountryProvinceForm);
}

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.classList.add('btn--loading');

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove('btn--loading');
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error blocks-radius grid gap-2">
        <p class="font-body-bolder m-0">${FoxTheme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert blocks-radius alert--${
        shippingRates.length === 0 ? 'error' : 'success'
      } grid gap-2 leading-tight">
        <p class="font-body-bolder m-0">${
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults
        }</p>
        ${
          shippingRatesList === ''
            ? ''
            : `<ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>`
        }
      </div>
    `;
  }
}
if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });
}

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(this.init.bind(this), { timeout: 3000 });
    } else {
      const userAgent = navigator.userAgent;
      const isSafari = userAgent.includes('Safari') && !userAgent.includes('Chrome');
      if (isSafari) {
        this.init();
      } else {
        FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
      }
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  init() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector('cart-drawer-products-recommendation');
        if (recommendations && recommendations.innerHTML.trim().length) {
          const productCount = recommendations.querySelectorAll('.product-card');
          this.innerHTML = recommendations.innerHTML;
          this.initCarousel();
          if (productCount.length > 0) this.classList.remove('hidden');
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        } else {
          this.closest('.shopify-section').remove();
          this.classList.add('hidden');
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  initCarousel() {
    this.carousel = new FoxTheme.Carousel(this.slideContainer, {
      slidesPerView: 1,
      spaceBetween: 10,
      loop: false,
      pagination: {
        el: this.sliderPagination,
        type: 'fraction',
      },
      navigation: {
        nextEl: this.sliderNext,
        prevEl: this.sliderPrev,
      },
    });

    this.carousel && this.carousel.init();
    if (this.slider) {
      this.slider.classList.add('is-initialized');
    }
  }
}
if (!customElements.get('cart-drawer-products-recommendation')) {
  customElements.define('cart-drawer-products-recommendation', CartDrawerProductsRecommendation);
}

class MainCart extends HTMLElement {
  constructor() {
    super();
    this.skeletonLoader = null;

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));

    // Apply skeleton loader on main cart page load
    if (window.location.pathname.includes('/cart')) {
      setTimeout(() => {
        this.applySkeletonLoader();
        setTimeout(() => this.removeSkeletonLoader(), 1500);
      }, 0);
    }

    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      (event) => this.onCartUpdate(event)
    );
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  applySkeletonLoader() {
    if (this.skeletonLoader) return;
    this.skeletonLoader = new MainCartSkeletonLoader(this);
    this.skeletonLoader.apply();
  }

  removeSkeletonLoader() {
    if (this.skeletonLoader) {
      this.skeletonLoader.remove();
      this.skeletonLoader = null;
    }
  }

  onCartUpdate(event) {
    // Clear the reference to the old skeleton instance, since its DOM nodes will be destroyed.
    this.skeletonLoader = null;

    const sectionId = FoxTheme.utils.getSectionId(this);
    const sectionHtml = event.cart.sections?.[sectionId];

    if (sectionHtml) {
      const newElement = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .getElementById(this.id);

      if (newElement) {
        this.innerHTML = newElement.innerHTML;
      }
    }

    // Handle empty cart state
    if (event.cart.item_count === 0) {
      const cartContainer = this.closest('.cart');
      if (cartContainer) {
        cartContainer.classList.add('is-empty');
      }
    }

    // Re-apply a new skeleton to the new content to make the animation appear continuous.
    this.applySkeletonLoader();

    setTimeout(() => {
      this.removeSkeletonLoader();
    }, 1500);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
}
if (!customElements.get('main-cart')) {
  customElements.define('main-cart', MainCart);
}

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };
    this.goal = Number(this.dataset.minimumAmount) * Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;
  }

  connectedCallback() {
    this.updateShippingGloal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:updated', (event) => {
      this.updateShippingGloal(event.detail.cart.items_subtotal_price);
    });
  }

  updateShippingGloal(amount) {
    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      this.querySelector(this.selectors.leftToSpend).innerHTML = FoxTheme.Currency.formatMoney(
        spend,
        this.money_format
      );
      this.classList.remove('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }
  }
}
if (!customElements.get('free-shipping-goal')) {
  customElements.define('free-shipping-goal', FreeShippingGoal);
}

window.FoxKitAddToCart = async (payload) => {
  if (!payload?.properties?.['_FoxKit offer']) return;

  const cartJson = await (
    await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig(),
    })
  ).json();
  cartJson['sections'] = payload['sections'];
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

  document.dispatchEvent(
    new CustomEvent('product-ajax:added', {
      detail: {
        product: payload,
      },
    })
  );
};

function logTokenWhenReady() {
  if (
    window.discountNinja &&
    window.discountNinja.Offer &&
    Array.isArray(window.discountNinja.Offer.Offers) &&
    window.discountNinja.Offer.Offers.length > 0
  ) {
    // console.log(window.discountNinja.Offer.Offers[0].Token);
    addDiscountTokensToCartItems();
  } else {
    setTimeout(logTokenWhenReady, 100); // Try again in 100ms
  }
}

// Wait for window load event before initializing Discount Ninja
window.addEventListener('load', function() {
  setTimeout(logTokenWhenReady, 500); // Delay slightly to ensure pageIsLoading is set to false first

  // Also ensure our custom buttons are properly integrated with Discount Ninja
  setTimeout(function() {
    if (window.discountNinja && window.discountNinja.DiscountCode) {
      setupDiscountNinjaButtons();
    }
  }, 1000);
});

document.addEventListener("la:dn:cart:updated", async function() {
  // console.log("Discount Ninja cart updated event received. Refreshing relevant components.");

  // Check for main cart and refresh it
  const mainCart = document.querySelector('main-cart');
  if (mainCart) {
    mainCart.applySkeletonLoader();
    setTimeout(async () => {
      try {
        const sections = [FoxTheme.utils.getSectionId(mainCart)];
        const sectionsUrl = `${window.location.pathname}?sections=${sections.join(',')}`;
        const sectionsResponse = await fetch(sectionsUrl);
        const sectionsData = await sectionsResponse.json();

        const cartResponse = await fetch(`${FoxTheme.routes.cart_url}.js`);
        const cartData = await cartResponse.json();
        cartData.sections = sectionsData;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
            sources: 'la-dn-event',
            cart: cartData
        });
      } catch (e) {
        console.error("Failed to refresh main cart from Discount Ninja event", e);
        mainCart.removeSkeletonLoader();
      }
    }, 1500);
  }

  // Check for cart drawer and refresh it
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: {
          open: false
        }
      }));
  }
});

function addDiscountTokensToCartItems() {
  if (
    window.discountNinja &&
    window.discountNinja.Offer &&
    Array.isArray(window.discountNinja.Offer.Offers)
  ) {
    const offers = window.discountNinja.Offer.Offers;
    document.querySelectorAll('.cart-item[data-product-handle]').forEach(cartItem => {
      const productHandle = cartItem.getAttribute('data-product-handle');
      const variantId = cartItem.getAttribute('data-variant-id');
      let matched = false;

      offers.forEach(offer => {
        const token = offer.Token;
        const offerType = offer.OfferType;
        const offerId = offer.Id || offer.Token;
        const entitledHandles = offer.Entitlement && offer.Entitlement.EntitledProductHandles
          ? offer.Entitlement.EntitledProductHandles.split(',').map(h => h.trim())
          : [];
        const entitledVariantIds = offer.Entitlement && offer.Entitlement.EntitledVariantIds
          ? offer.Entitlement.EntitledVariantIds.split(',').map(id => id.trim())
          : [];

        const isAssociatedByHandle = entitledHandles.includes(productHandle);
        const isAssociatedByVariant = entitledVariantIds.includes(variantId);
        const isAssociated = isAssociatedByHandle || isAssociatedByVariant;

        if (isAssociated) {
          cartItem.setAttribute('data-discount-token', token);
          cartItem.setAttribute('data-discount-type', offerType);
          cartItem.setAttribute('data-discount-offer-id', offerId);
          cartItem.classList.add('is-discounted');
          matched = true;
        }
      });

      if (!matched) {
        cartItem.removeAttribute('data-discount-token');
        cartItem.removeAttribute('data-discount-type');
        cartItem.removeAttribute('data-discount-offer-id');
        cartItem.classList.remove('is-discounted');
      }
    });
  }
}

function setupDiscountNinjaButtons() {
  // console.log('Setting up Discount Ninja buttons');

  // Always try to set up buttons regardless of drawer state
  const applyButton = document.getElementById('discount-ninja-apply-btn');
  const discountNinjaApplyButton = document.querySelector('.limoniapps-discountninja-cartdiscountfield-apply:not(#discount-ninja-apply-btn)');
  const cancelButton = document.getElementById('discount-ninja-cancel-btn');

  // console.log('Found buttons:', {
  //   applyButton: applyButton,
  //   discountNinjaApplyButton: discountNinjaApplyButton,
  //   cancelButton: cancelButton
  // });

  // First, make sure we have a Discount Ninja input field
  const discountInput = document.querySelector('.limoniapps-discountninja-cartdiscountfield-input') || initializeDiscountNinjaField();
  // console.log('Discount input field:', discountInput);

  if (applyButton) {
    // console.log('Setting up custom apply button');

    // Remove existing event listeners using a more reliable method
    applyButton.outerHTML = applyButton.outerHTML;

    // Get the new button reference after replacing the HTML
    const newApplyButton = document.getElementById('discount-ninja-apply-btn');

    // Add direct click handler with debugging
    if (newApplyButton) {
      // console.log('Adding click handler to button');
      newApplyButton.onclick = function(e) {
        // console.log('Custom apply button clicked (onclick)');
        e.preventDefault();
        e.stopPropagation();
        handleApplyButtonClick(e);
        return false;
      };

      // Add a second handler with addEventListener for redundancy
      newApplyButton.addEventListener('click', function(e) {
        // console.log('Custom apply button clicked (addEventListener)');
        e.preventDefault();
        e.stopPropagation();
        handleApplyButtonClick(e);
      });

      // Add a global click handler for this button as a fallback
      document.addEventListener('click', function(event) {
        const target = event.target;
        if (target && (target.id === 'discount-ninja-apply-btn' ||
            (target.closest && target.closest('#discount-ninja-apply-btn')))) {
          // console.log('Custom apply button clicked (global handler)');
          event.preventDefault();
          event.stopPropagation();
          handleApplyButtonClick(event);
        }
      });
    }
  }

  if (discountNinjaApplyButton) {
    // console.log('Setting up Discount Ninja native apply button');
    discountNinjaApplyButton.removeEventListener('click', handleApplyButtonClick);
    discountNinjaApplyButton.addEventListener('click', handleApplyButtonClick);
    discountNinjaApplyButton.setAttribute('data-event-bound', 'true');
  }

  if (cancelButton) {
    // console.log('Setting up cancel button');

    // Remove existing event listeners using a more reliable method
    cancelButton.outerHTML = cancelButton.outerHTML;

    // Get the new button reference after replacing the HTML
    const newCancelButton = document.getElementById('discount-ninja-cancel-btn');

    // Add direct click handler
    if (newCancelButton) {
      newCancelButton.onclick = function(e) {
        // console.log('Cancel button clicked');
        e.preventDefault();
        e.stopPropagation();
        handleCancelButtonClick(e);
        return false;
      };
    }
  }

  // Ensure Discount Ninja placeholder is initialized
  const placeholder = document.querySelector('.limoniapps-discountninja-cartdiscountfield-placeholder');
  if (placeholder && !placeholder.querySelector('.limoniapps-discountninja-cartdiscountfield-input')) {
    // console.log('Initializing Discount Ninja placeholder');
    if (window.discountNinja && window.discountNinja.DiscountCode && window.discountNinja.DiscountCode.Initialize) {
      window.discountNinja.DiscountCode.Initialize();
    } else {
      // If Discount Ninja's Initialize function isn't available, use our manual initialization
      initializeDiscountNinjaField();
    }
  }
}

// Make handleApplyButtonClick and handleCancelButtonClick globally available
window.handleApplyButtonClick = async function(event) {
  event.preventDefault();
  event.stopPropagation();
  // console.log('Apply button clicked - handleApplyButtonClick function executing');

  try {
    // Try to find the input field with more selectors
    const discountInput = document.querySelector('.limoniapps-discountninja-cartdiscountfield-input') ||
                          document.querySelector('[placeholder="Enter your code here"]') ||
                          document.querySelector('input[type="text"][class*="discount"]');

    // console.log('Found discount input:', discountInput);

    if (!discountInput) {
      console.error('No discount input field found');
      alert('No discount input field found. Please try refreshing the page.');
      return;
    }

    if (!discountInput.value.trim()) {
      // console.log('No discount code entered');
      const messageEl = document.createElement('div');
      messageEl.className = 'alert alert--error blocks-radius mt-2 mb-2 p-2 text-sm';
      messageEl.textContent = 'Please enter a discount code';

      const container = discountInput.closest('.cart__footer--discount-code') ||
                        discountInput.closest('.limoniapps-discountninja-cartdiscountfield-inputandbutton') ||
                        discountInput.parentNode;

      if (container && !container.querySelector('.alert')) {
        container.appendChild(messageEl);

        setTimeout(() => {
          if (container.contains(messageEl)) {
            container.removeChild(messageEl);
          }
        }, 3000);
      }

      discountInput.focus();

      return;
    }

    const discountCode = discountInput.value.trim();

    // Try to use the native Discount Ninja functions if available
    if (window.discountNinja && window.discountNinja.DiscountCode && window.discountNinja.DiscountCode.Apply) {
      // console.log('Using native Discount Ninja Apply function');
      try {
        window.discountNinja.DiscountCode.Apply(discountInput);
        // console.log('Discount Ninja Apply function called successfully');
        return;
      } catch (dnError) {
        // console.error('Error using Discount Ninja Apply function:', dnError);
      }
    }

    // Fall back to the API method if the direct function isn't available
    if (window.discountNinja && window.discountNinja.api && window.discountNinja.api.discountCode) {
      // console.log('Using Discount Ninja API');
      try {
        await window.discountNinja.api.discountCode.add(discountCode);
        // console.log('Discount code applied successfully via API');
        setTimeout(() => refreshCartAfterDiscountChange(), 500);
        return;
      } catch (error) {
        // console.error('Error applying discount code via API:', error);
      }
    }

    // console.log('Falling back to direct fetch method');
    // Last resort - try to submit the discount via fetch
    try {
      // console.log('Applying discount code via fetch:', discountCode);
      const response = await fetch('/discount/' + encodeURIComponent(discountCode));
      // console.log('Manual discount application response:', response);

      if (response.ok) {
        // console.log('Discount applied successfully via fetch');
        alert('Discount code applied successfully!');
        setTimeout(() => {
          // console.log('Refreshing cart after discount change');
          window.location.reload();
        }, 500);
      } else {
        // console.error('Error applying discount code via fetch - response not OK');
        alert('There was an error applying your discount code. Please try again.');
      }
    } catch (fetchError) {
      // console.error('Error applying discount code via fetch:', fetchError);
      alert('Discount system is not available. Please try refreshing the page.');
    }
  } catch (error) {
    // console.error('Error in handleApplyButtonClick:', error);
    alert('An error occurred while applying the discount code. Please try again.');
  }
};

window.handleCancelButtonClick = async function(event) {
  event.preventDefault();
  event.stopPropagation();
  // console.log('Cancel button clicked - handleCancelButtonClick function executing');

  try {
    const discountInput = document.querySelector('.limoniapps-discountninja-cartdiscountfield-input');

    if (!discountInput) {
      // console.error('No discount input field found');
      return;
    }

    discountInput.value = '';

    const appliedDiscountTag = document.querySelector('.limoniapps-discountninja-cartdiscountfield-discountcode');

    if (appliedDiscountTag) {
      // console.log('Found applied discount tag, removing it');
      const discountCodeElement = appliedDiscountTag.querySelector('.limoniapps-discountninja-cartdiscountfield-discountcode-code');
      const discountCode = discountCodeElement ? discountCodeElement.textContent.trim() : '';

      if (window.discountNinja && window.discountNinja.DiscountCode && window.discountNinja.DiscountCode.RemoveTag) {
        try {
          // console.log('Using Discount Ninja RemoveTag function');
          window.discountNinja.DiscountCode.RemoveTag(appliedDiscountTag);

          setTimeout(() => {
            refreshCartAfterDiscountChange();
          }, 1000);

          return;
        } catch (dnError) {
          // console.error('Error using Discount Ninja RemoveTag function:', dnError);
        }
      }

      if (window.discountNinja && window.discountNinja.api && window.discountNinja.api.discountCode) {
        try {
          // console.log('Using Discount Ninja API to remove discount');
          if (discountCode && window.discountNinja.api.discountCode.remove) {
            await window.discountNinja.api.discountCode.remove(discountCode);
          } else {
            await window.discountNinja.api.discountCode.remove();
          }

          await new Promise(resolve => setTimeout(resolve, 1000));

          refreshCartAfterDiscountChange();
          return;
        } catch (apiError) {
          // console.error('Error using Discount Ninja API to remove discount:', apiError);
        }
      }

      try {
        // console.log('Manually removing discount tag from DOM');
        appliedDiscountTag.remove();

        refreshCartAfterDiscountChange();
        return;
      } catch (domError) {
        // console.error('Error manually removing discount tag:', domError);
      }
    }

    if (window.discountNinja && window.discountNinja.api && window.discountNinja.api.discountCode) {
      try {
        // console.log('Checking for any active discount codes');
        if (window.discountNinja.api.discountCode.list && window.discountNinja.api.discountCode.list().length > 0) {
          // console.log('Found active discount codes, removing them');
          await window.discountNinja.api.discountCode.remove();

          await new Promise(resolve => setTimeout(resolve, 1000));

          refreshCartAfterDiscountChange();
        }
      } catch (error) {
        console.error('Error checking/removing discount codes:', error);
      }
    } else if (window.discountNinja && window.discountNinja.DiscountCode && window.discountNinja.DiscountCode.RemoveTag) {
      // console.log('Using Discount Ninja RemoveTag function with null parameter');
      window.discountNinja.DiscountCode.RemoveTag(null);

      setTimeout(() => {
        refreshCartAfterDiscountChange();
      }, 1000);
    }
  } catch (error) {
    // console.error('Error in handleCancelButtonClick:', error);
    setTimeout(() => {
      refreshCartAfterDiscountChange();
    }, 1000);
  }
};

document.addEventListener('click', function(event) {
  // Manually handle cart drawer close button and overlay clicks because the
  // component's native event listeners can be lost when we replace its innerHTML after an update.
  const cartDrawer = document.querySelector('cart-drawer.drawer--active');
  if (!cartDrawer) return;

  const closeButton = event.target.closest('.drawer__close-btn');
  const isCloseButtonForThisDrawer = closeButton && closeButton.getAttribute('aria-controls') === cartDrawer.id;

  const overlay = event.target.closest('.fixed-overlay');
  const isOverlayForThisDrawer = overlay && overlay.getAttribute('aria-controls') === cartDrawer.id;

  if (isCloseButtonForThisDrawer || isOverlayForThisDrawer) {
    event.preventDefault();
    cartDrawer.hide();
  }
});

async function refreshCartAfterDiscountChange() {
  try {
    const cartItems = document.querySelectorAll('.cart-item');
    cartItems.forEach(item => {
      const priceElements = item.querySelectorAll('.price, .cart-item__prices, .price__regular, .price__sale');
      priceElements.forEach(priceEl => {
        if (!priceEl.dataset.originalContent) {
          priceEl.dataset.originalContent = priceEl.innerHTML;
        }
        priceEl.innerHTML = '<span class="skeleton-price skeleton-line shimmer"></span>';
      });

      const totalPriceWrapper = item.querySelector('.cart-item__action .cart-item__prices');
      if (totalPriceWrapper && !totalPriceWrapper.dataset.originalContent) {
        totalPriceWrapper.dataset.originalContent = totalPriceWrapper.innerHTML;
        totalPriceWrapper.innerHTML = '<span class="skeleton-item-total skeleton-line shimmer"></span>';
      }

      const discountNinjaPrices = item.querySelectorAll('.limoniapps-discountninja-cart-item-line-price');
      discountNinjaPrices.forEach(dnPrice => {
        if (!dnPrice.dataset.originalContent) {
          dnPrice.dataset.originalContent = dnPrice.innerHTML;
        }
        dnPrice.innerHTML = '<span class.skeleton-dn-price skeleton-line shimmer"></span>';
      });

      const discountElements = item.querySelectorAll('.cart-item__discounts, .discount');
      discountElements.forEach(discountEl => {
        if (!discountEl.dataset.originalContent) {
          discountEl.dataset.originalContent = discountEl.innerHTML;
        }
        discountEl.innerHTML = '<span class="skeleton-discount skeleton-line shimmer"></span>';
      });
    });

    const totalColumnHeaders = document.querySelectorAll('.cart__item-total');
    totalColumnHeaders.forEach(header => {
      if (!header.dataset.originalContent) {
        header.dataset.originalContent = header.innerHTML;
        if (header.tagName === 'TH') {
          header.innerHTML = header.dataset.originalContent;
        } else {
          header.innerHTML = '<span class="skeleton-table-total skeleton-line shimmer"></span>';
        }
      }
    });

    const cartItemTotals = document.querySelectorAll('.cart-item__total');
    cartItemTotals.forEach(total => {
      if (!total.dataset.originalContent) {
        total.dataset.originalContent = total.innerHTML;
        total.innerHTML = '<span class="skeleton-cart-item-total skeleton-line shimmer"></span>';
      }
    });

    const dnLinePrice = document.querySelectorAll('[data-la-dn-cart-item-line-price]');
    dnLinePrice.forEach(element => {
      if (!element.dataset.originalContent) {
        element.dataset.originalContent = element.innerHTML;
        element.innerHTML = '<span class="skeleton-dn-line-price skeleton-line shimmer"></span>';
      }
    });

    const totalsElements = document.querySelectorAll('.totals__subtotal-value, .tax-note');
    totalsElements.forEach(totalEl => {
      if (!totalEl.dataset.originalContent) {
        totalEl.dataset.originalContent = totalEl.innerHTML;
      }
      totalEl.innerHTML = '<span class="skeleton-total skeleton-line shimmer"></span>';
    });

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const cartResponse = await fetch(`${FoxTheme.routes.cart_url}.js`);

    if (!cartResponse.ok) {
      throw new Error(`Cart fetch failed: ${cartResponse.status}`);
    }

    const cartData = await cartResponse.json();

    const sectionsUrl = `${FoxTheme.routes.cart_url}?sections=${sectionsToBundle.join(',')}`;
    const sectionsResponse = await fetch(sectionsUrl);

    if (!sectionsResponse.ok) {
      throw new Error(`Sections fetch failed: ${sectionsResponse.status}`);
    }

    const sectionsData = await sectionsResponse.json();

    cartData.sections = sectionsData;

    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      sources: 'discount-code-cancel',
      cart: cartData
    });

    if (window.discountNinja?.api?.strikethroughPricing?.apply) {
      await window.discountNinja.api.strikethroughPricing.apply();
    }

    addDiscountTokensToCartItems();

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cart: cartData }
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        detail: {
          open: false,
          isRefreshing: true
        }
      })
    );

    setTimeout(() => {
      document.querySelectorAll('[data-original-content]').forEach(element => {
        element.innerHTML = element.dataset.originalContent;
        delete element.dataset.originalContent;
      });
    }, 1500);

  } catch (error) {
  }
}

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  setupDiscountNinjaButtons();
} else {
}

document.addEventListener('DOMContentLoaded', function() {
  // console.log('DOMContentLoaded: Setting up Discount Ninja integration');

  // Set up buttons immediately
  setupDiscountNinjaButtons();

  // Set up a MutationObserver to detect when Discount Ninja adds its elements
  const discountContainer = document.querySelector('.discount-ninja-container');
  if (discountContainer) {
    // console.log('Setting up MutationObserver for Discount Ninja container');
    const observer = new MutationObserver(function(mutations) {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          // console.log('Discount Ninja container updated, setting up buttons');
          setupDiscountNinjaButtons();
          break;
        }
      }
    });

    observer.observe(discountContainer, { childList: true, subtree: true });
  }

  // Also set up buttons when cart is updated
  document.addEventListener('cart:updated', function() {
    // console.log('Cart updated, setting up Discount Ninja buttons');
    setTimeout(setupDiscountNinjaButtons, 300);
  });

  // Set up buttons when Discount Ninja is fully loaded
  if (window.discountNinja) {
    // console.log('Discount Ninja already loaded');
    setupDiscountNinjaButtons();
  } else {
    // Check periodically for Discount Ninja to be loaded
    const checkForDiscountNinja = setInterval(function() {
      if (window.discountNinja) {
        // console.log('Discount Ninja loaded, setting up buttons');
        setupDiscountNinjaButtons();
        clearInterval(checkForDiscountNinja);
      }
    }, 500);

    // Stop checking after 10 seconds
    setTimeout(function() {
      clearInterval(checkForDiscountNinja);
    }, 10000);
  }
});

document.addEventListener('click', function(event) {
  try {
    if (!window.location.pathname.includes('/cart') && document.querySelector('.cart-drawer.drawer--active')) {
      return;
    }

    const isCancelButton =
      (event.target.id === 'discount-ninja-cancel-btn') ||
      (event.target.closest && event.target.closest('#discount-ninja-cancel-btn')) ||
      (event.target.classList && event.target.classList.contains('btn-discount--cancel')) ||
      (event.target.closest && event.target.closest('.btn-discount--cancel'));

    if (isCancelButton) {
      event.preventDefault();
      event.stopPropagation();
      handleCancelButtonClick(event);
    }
  } catch (error) {
    console.error('Error in cancel button click handler:', error);
  }
});

document.addEventListener('DOMContentLoaded', function() {
  const skeletonStyles = document.createElement('style');
  skeletonStyles.textContent = `
    /* Base skeleton styles */
    .skeleton-line {
      height: 16px;
      background: #e0e0e0;
      margin: 6px 0;
      border-radius: 4px;
      display: block !important;
      opacity: 1 !important;
      visibility: visible !important;
    }
    
    /* Main Cart Skeleton Styles */
    .main-cart .skeleton-line {
      background: linear-gradient(to right, #eeeeee 0%, #dddddd 50%, #eeeeee 100%);
      background-size: 200% 100%;
      animation: mainCartShimmer 1.5s infinite;
    }
    
    /* Cart Drawer Skeleton Styles */
    .cart-drawer .skeleton-line {
      background: linear-gradient(to right, #eeeeee 0%, #c9c9c9 50%, #eeeeee 100%);
      background-size: 200% 100%;
      animation: drawerShimmer 1.2s infinite;
      position: relative;
      z-index: 5;
    }
    
    /* Preserve heights to prevent layout shifts */
    .cart-item__prices {
      min-height: 50px !important;
    }
    
    .cart-item__action .cart-item__prices {
      min-height: 70px !important;
    }
    
    .price, .price__regular, .price__sale {
      min-height: 24px !important;
      display: block !important;
    }
    
    .totals__subtotal-value {
      min-height: 24px !important;
      display: block !important;
    }
    
    .tax-note {
      min-height: 20px !important;
      display: block !important;
    }
    
    /* Skeleton size variants */
    .skeleton-price { width: 80%; min-height: 18px; }
    .skeleton-discount { width: 60%; min-height: 16px; }
    .skeleton-total { width: 100%; min-height: 24px; }
    .skeleton-item-total { width: 100%; height: 24px; }
    .skeleton-dn-price { width: 100%; height: 24px; }
    .skeleton-table-total { width: 100%; height: 24px; }
    .skeleton-cart-item-total { width: 100%; height: 24px; }
    .skeleton-dn-line-price { width: 100%; height: 24px; }
    .skeleton-dynamic-price { width: 100%; height: 24px; }
    
    .skeleton-container {
      min-height: 20px;
      background: linear-gradient(to right, #eeeeee 0%, #dddddd 50%, #eeeeee 100%);
      background-size: 200% 100%;
      animation: mainCartShimmer 1.5s infinite;
      border-radius: 4px;
    }
    
    .skeleton-container > * {
      visibility: hidden;
    }
    
    /* Cart drawer specific styles to preserve layout */
    .cart-drawer .cart-item__prices {
      min-height: 70px !important;
    }
    
    .cart-drawer .price {
      min-height: 24px !important;
    }
    
    .cart-drawer .totals__subtotal-value,
    .cart-drawer .tax-note {
      min-height: 24px !important;
    }
    
    @keyframes mainCartShimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    
    @keyframes drawerShimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(skeletonStyles);
});

class MainCartSkeletonLoader {
  constructor(cartElement) {
    this.cartElement = cartElement;
    this.container = cartElement.closest('.cart');
  }

  _createOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'skeleton-overlay';
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: white;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
    overlay.innerHTML = '<span class="skeleton-line" style="width: 80%; height: 1.5em; margin: auto;"></span>';
    return overlay;
  }

  apply() {
    const elementsToOverlay = [
      ...this.container.querySelectorAll('.cart-item__price .price'),
      ...this.container.querySelectorAll('.cart-item__total'),
      ...this.container.querySelectorAll('.cart-item__product--info-mobile-right .price'),
      ...this.container.querySelectorAll('.cart__footer .totals__subtotal-value'),
      ...this.container.querySelectorAll('.cart__footer .totals__total-savings-value')
    ];

    elementsToOverlay.forEach(el => {
      if (el && !el.querySelector('.skeleton-overlay')) {
        el.style.position = 'relative'; // Necessary for absolute positioning of overlay
        el.appendChild(this._createOverlay());
      }
    });

    const dnContainers = this.container.querySelectorAll('.discount-ninja-cart-text-container');
    dnContainers.forEach(container => {
      container.classList.add('skeleton-container');
    });
  }

  remove() {
    this.container.querySelectorAll('.skeleton-overlay').forEach(overlay => {
      overlay.remove();
    });
    // Clean up the position style we added
    this.container.querySelectorAll('[style*="position: relative"]').forEach(el => {
      if (el.style.position === 'relative') {
        el.style.position = '';
      }
    });

    const dnContainers = this.container.querySelectorAll('.discount-ninja-cart-text-container');
    dnContainers.forEach(container => {
      container.classList.remove('skeleton-container');
    });
  }
}

class CartDrawerSkeletonLoader {
  constructor(drawerElement) {
    this.drawerElement = drawerElement;
  }

  apply() {
    const cartItems = this.drawerElement.querySelectorAll('.cart-item');
    if (cartItems.length > 0) {
      cartItems.forEach(item => {
        this.applyToPriceElements(item);
        this.applyToDiscountElements(item);
        this.applyToDynamicElements(item);
      });
    }
    // this.applyToTotalElements(this.drawerElement);
  }

  applyToPriceElements(item) {
    const priceContainer = item.querySelector('.cart-item__prices-content');
    if (priceContainer && !priceContainer.querySelector('.skeleton-overlay')) {
      priceContainer.style.position = 'relative';

      const overlay = document.createElement('div');
      overlay.className = 'skeleton-overlay';
      overlay.style.position = 'absolute';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.right = '0';
      overlay.style.bottom = '0';
      overlay.style.background = '#FFFFFF';
      overlay.style.zIndex = '10';
      overlay.style.display = 'flex';
      overlay.style.flexDirection = 'column';
      overlay.style.alignItems = 'flex-end';
      overlay.style.justifyContent = 'space-around';
      overlay.style.padding = '.5rem .5rem';

      overlay.innerHTML = `
        <span class="skeleton-line" style="width: 75%; height: 18px;"></span>
        <span class="skeleton-line" style="width: 90%; height: 14px;"></span>
      `;

      priceContainer.appendChild(overlay);
    }
  }

  applyToDiscountElements(item) {
    const discountElements = item.querySelectorAll('.cart-item__discounts, .discount');
    discountElements.forEach(discountEl => {
      if (!discountEl.dataset.originalContent) {
        discountEl.dataset.originalContent = discountEl.innerHTML;
        if (!discountEl.dataset.originalHeight && discountEl.offsetHeight > 0) {
          discountEl.dataset.originalHeight = discountEl.offsetHeight + 'px';
          discountEl.style.minHeight = discountEl.dataset.originalHeight;
        }
      }
      discountEl.innerHTML = '<span class="skeleton-discount skeleton-line"></span>';
    });
  }

  applyToTotalElements(item) {
    const totalElements = item.querySelectorAll('.totals__subtotal-value, .tax-note');
    totalElements.forEach(totalEl => {
      if (!totalEl.dataset.originalContent) {
        totalEl.dataset.originalContent = totalEl.innerHTML;
        if (!totalEl.dataset.originalHeight && totalEl.offsetHeight > 0) {
          totalEl.dataset.originalHeight = totalEl.offsetHeight + 'px';
          totalEl.style.minHeight = totalEl.dataset.originalHeight;
        }
      }
      totalEl.innerHTML = '<span class="skeleton-total skeleton-line"></span>';
    });
  }

  applyToDynamicElements(item) {
    const dynamicPrices = item.querySelectorAll('[data-la-dn]');
    dynamicPrices.forEach(priceEl => {
      if (!priceEl.querySelector('.skeleton-line') && !priceEl.dataset.originalContent) {
        priceEl.dataset.originalContent = priceEl.innerHTML;
        if (!priceEl.dataset.originalHeight && priceEl.offsetHeight > 0) {
          priceEl.dataset.originalHeight = priceEl.offsetHeight + 'px';
          priceEl.style.minHeight = priceEl.dataset.originalHeight;
        }
        priceEl.innerHTML = '<span class="skeleton-dynamic-price skeleton-line"></span>';
      }
    });
  }

  remove() {
    this.drawerElement.querySelectorAll('[data-original-content]').forEach(element => {
      if (element.dataset.originalContent) {
        element.innerHTML = element.dataset.originalContent;
        delete element.dataset.originalContent;
      }
    });

    this.drawerElement.querySelectorAll('.skeleton-overlay').forEach(overlay => {
      overlay.remove();
    });
  }
}

// Function to manually initialize the Discount Ninja input field if it doesn't exist
function initializeDiscountNinjaField() {
  // console.log('Initializing Discount Ninja field');
  const placeholder = document.querySelector('.limoniapps-discountninja-cartdiscountfield-placeholder');
  const container = document.querySelector('.discount-ninja-container');

  if (placeholder && !placeholder.querySelector('.limoniapps-discountninja-cartdiscountfield-input')) {
    // console.log('Creating Discount Ninja input field in placeholder');

    // Create a basic input field structure similar to what Discount Ninja would create
    const wrapper = document.createElement('div');
    wrapper.className = 'limoniapps-discountninja-cartdiscountfield-wrapper';
    wrapper.setAttribute('version', '1.0.0');

    const inner = document.createElement('div');
    inner.className = 'limoniapps-discountninja-cartdiscountfield-inner';

    const inputAndButton = document.createElement('div');
    inputAndButton.className = 'limoniapps-discountninja-cartdiscountfield-inputandbutton';

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'limoniapps-discountninja-cartdiscountfield-input';
    input.value = '';
    input.autocomplete = 'off';
    input.placeholder = 'Enter your code here';

    // Add the native apply button (separate from our custom one)
    const applyBtn = document.createElement('button');
    applyBtn.type = 'button';
    applyBtn.className = 'limoniapps-discountninja-cartdiscountfield-apply';
    applyBtn.textContent = 'Apply';

    // Assemble the structure
    inputAndButton.appendChild(input);
    inputAndButton.appendChild(applyBtn);
    inner.appendChild(inputAndButton);
    wrapper.appendChild(inner);
    placeholder.appendChild(wrapper);

    // Set up the event handler
    applyBtn.addEventListener('click', handleApplyButtonClick);

    return input;
  } else if (container && !document.querySelector('.limoniapps-discountninja-cartdiscountfield-input')) {
    // console.log('Creating Discount Ninja input field in container');

    // Create placeholder if it doesn't exist
    if (!placeholder) {
      const newPlaceholder = document.createElement('div');
      newPlaceholder.className = 'limoniapps-discountninja-cartdiscountfield-placeholder';
      container.appendChild(newPlaceholder);

      // Call this function again now that we have a placeholder
      return initializeDiscountNinjaField();
    }
  }

  return document.querySelector('.limoniapps-discountninja-cartdiscountfield-input');
}
